import { enableProdMode, ErrorHandler, importProvidersFrom, provideZoneChangeDetection } from '@angular/core';
import { HTTP_INTERCEPTORS, withInterceptorsFromDi, provideHttpClient } from '@angular/common/http';
import { provideNoopAnimations } from '@angular/platform-browser/animations';
import { BrowserModule, bootstrapApplication } from '@angular/platform-browser';
import { Route, provideRouter, withDisabledInitialNavigation, withEnabledBlockingInitialNavigation, withRouterConfig } from '@angular/router';
import { MsalInterceptor, MSAL_INSTANCE, MsalInterceptorConfiguration, MsalGuardConfiguration, MSAL_GUARD_CONFIG, MSAL_INTERCEPTOR_CONFIG, MsalService, MsalGuard, MsalBroadcastService } from '@azure/msal-angular';
import { IPublicClientApplication, PublicClientApplication, InteractionType, BrowserCacheLocation, LogLevel, BrowserUtils } from '@azure/msal-browser';
import { AppComponent } from './app/app.component';
import { FailedLoginRequestComponent } from './app/failed-login-request/failed-login-request.component';
import { ProfileComponent } from './app/profile/profile.component';
import { CustomerCallRecordsComponent } from './app/customer-call-records/customer-call-records.component';
import { FormsModule } from '@angular/forms';
import { environment } from './environments/environment';
import { MyMonitoringService } from './services/logging.service';
import { ErrorHandlerService } from './services/errorHandlerService.service';
import { DatePipe } from '@angular/common';
import { DistrictsComponent } from './app/districts/districts.component';
import { CallByDateComponent } from './app/call-by-date/callbydate.component';
import { CallByDistrictsComponent } from './app/call-by-districts/callbydistricts.component';
import { CallByAnalystStatusComponent } from './app/call-by-analyst-status/callbyanalyst.component';
import { CallByDispatchStatusComponent } from './app/call-by-dispatch-status/call-by-dispatch.component';
import { CallByOrderTypeComponent } from './app/call-by-order-type/call-by-order-type.component';
import { CallBySubmitterComponent } from './app/call-by-submitter/call-by-submitter.component';
import { OrderEntryComponent } from './app/order-entry/order-entry.component';
import { OrdersByDistrictInSapComponent } from './app/orders-by-district-in-sap/orders-by-district-in-sap.component';
import { OrdersByDateModifiedComponent } from './app/orders-by-date-modified/orders-by-date-modified.component';
import { OrdersByNameComponent } from './app/orders-by-name/orders-by-name.component';

import { providePrimeNG } from 'primeng/config';
import Lara from '@primeuix/themes/lara';
import { definePreset, palette } from '@primeuix/themes';

const LaraBlue = definePreset(Lara, {
  semantic: {
    primary: palette('{blue}'),
  }
});

export function loggerCallback(logLevel: LogLevel, message: string) {
}

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.msalConfig.auth.clientId,
      authority: environment.msalConfig.auth.authority,
      redirectUri: environment.msalConfig.auth.redirectUri
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage
    },
    system: {
      allowNativeBroker: false, // Disables WAM Broker
      loggerOptions: {
        //loggerCallback,
        logLevel: LogLevel.Info,
        piiLoggingEnabled: false
      }
    }
  });
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();
  protectedResourceMap.set(environment.apiConfig.uri, environment.apiConfig.scopes);
  protectedResourceMap.set('/api/', environment.apiConfig1.scopes);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: ['user.read']
    },
    loginFailedRoute: '/failed-login-request'
  };
}

const initialNavigation = !BrowserUtils.isInIframe() && !BrowserUtils.isInPopup()
  ? withEnabledBlockingInitialNavigation() // Set to enabledBlocking to use Angular Universal
  : withDisabledInitialNavigation();

export const Routes: Route[] = [
  {
    path: 'profile',
    component: ProfileComponent,
    canActivate: [MsalGuard]
  },
  {
    path: '',
    redirectTo: 'home',  // Redirect empty path to home
    pathMatch: 'full'
  },
  {
    path: 'failed-login-request',
    component: FailedLoginRequestComponent
  },
  {
    path: 'home',
    component: CustomerCallRecordsComponent,
    data: { title: '' }
  },
  {
    path: 'order-entry',
    component: OrderEntryComponent,
    data: { title: 'Order Entry' }
  },
  {
    path: 'order-entry/:ccrId',
    component: OrderEntryComponent,
    data: { title: 'Order Update' }
  },

  {
    path: 'orders-by-district-in-sap',
    component: OrdersByDistrictInSapComponent,
    data: { title: 'Orders by:  District, In SAP' }

  },
  {
    path: 'orders-by-date-modified',
    component: OrdersByDateModifiedComponent,
    data: { title: 'Orders by:  Date Modified' }

  },
  {
    path: 'orders-by-name',
    component: OrdersByNameComponent,
    data: { title: 'Orders by:  Modified by Name' }

  },
  {
    path: 'bycalldate',
    component: CallByDateComponent,
    data: { title: 'By Call Date' }
  },
  {
    path: 'bydistricts',
    component: CallByDistrictsComponent,
    data: { title: 'By Districts' }
  },
  {
    path: 'byanalyststatus',
    component: CallByAnalystStatusComponent,
    data: { title: 'By Analyst Status' }
  },
  {
    path: 'bydispatchstatus',
    component: CallByDispatchStatusComponent,
    data: { title: 'By Dispatch Status' }
  },
  {
    path: 'bysubmitter',
    component: CallBySubmitterComponent,
    data: { title: 'By Submitter' }
  },
  {
    path: 'bydispatchstatus',
    component: CallByDispatchStatusComponent,
    data: { title: 'By Dispatch Status' }
  },
  {
    path: 'bytype',
    component: CallByOrderTypeComponent,
    data: { title: 'By Type' }
  },
  {
    path: 'districts',
    component: DistrictsComponent,
    data: { title: 'Order By Districts' }
  }
];


bootstrapApplication(AppComponent, {
  providers: [
    provideZoneChangeDetection(), importProvidersFrom(BrowserModule, FormsModule),
    provideRouter(Routes, initialNavigation, withRouterConfig({ onSameUrlNavigation: 'reload' })),
    provideNoopAnimations(),
    provideHttpClient(withInterceptorsFromDi()),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    MyMonitoringService,
    {
      provide: ErrorHandler, useClass: ErrorHandlerService
    },
    providePrimeNG({
      theme: {
        preset: LaraBlue,
        options: {
          darkModeSelector: '.dark-mode',
          cssLayer: true
        }
      }
    })
  ]
})
  .catch(err => console.error(err));

